import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FloatingShapes from '@/components/FloatingShapes';

const BENEFITS = [
  {
    icon: 'ri-speed-up-line',
    titleKey: 'about.benefits.b1.title',
    textKey: 'about.benefits.b1.text',
    accent: 'accent',
  },
  {
    icon: 'ri-scissors-cut-line',
    titleKey: 'about.benefits.b2.title',
    textKey: 'about.benefits.b2.text',
    accent: 'secondary',
  },
  {
    icon: 'ri-shield-check-line',
    titleKey: 'about.benefits.b3.title',
    textKey: 'about.benefits.b3.text',
    accent: 'accent',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    titleKey: 'about.benefits.b4.title',
    textKey: 'about.benefits.b4.text',
    accent: 'secondary',
  },
  {
    icon: 'ri-git-merge-line',
    titleKey: 'about.benefits.b5.title',
    textKey: 'about.benefits.b5.text',
    accent: 'accent',
  },
  {
    icon: 'ri-line-chart-line',
    titleKey: 'about.benefits.b6.title',
    textKey: 'about.benefits.b6.text',
    accent: 'secondary',
  },
];

export default function AboutBenefitsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-14 md:py-16 bg-background-50 overflow-hidden">
      <FloatingShapes count={16} color="accent" speed={0.5} />
      <div className="absolute inset-0 pointer-events-none">
        <FloatingShapes count={10} color="secondary" speed={0.5} />
      </div>

      <div className="absolute top-0 left-0 w-80 h-80 bg-accent-500/5 rounded-full blur-3xl animate-[morphPulse_7s_ease-in-out_infinite]" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-500/6 rounded-full blur-3xl animate-[morphFloat_8s_ease-in-out_infinite_1s]" />

      <div className="relative w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div
            className={`text-center mb-12 transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <span className="inline-block px-3 py-1 bg-accent-100/80 rounded-full text-[10px] font-mono font-medium text-accent-700 uppercase tracking-widest mb-4">
              {t('about.benefits.badge')}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground-950 leading-tight tracking-tight max-w-3xl mx-auto">
              {t('about.benefits.title')}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto mt-4">
              {t('about.benefits.paragraph')}
            </p>
          </div>

          {/* Benefits grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {BENEFITS.map((benefit, i) => (
              <div
                key={benefit.titleKey}
                className={`relative p-6 md:p-7 rounded-xl border border-background-200/80 bg-background-50/80 backdrop-blur-sm transition-all duration-700 hover:-translate-y-1 hover:shadow-lg hover:shadow-background-200/50 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className={`w-11 h-11 rounded-lg flex items-center justify-center mb-4 ${
                    benefit.accent === 'accent'
                      ? 'bg-accent-100 text-accent-600'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}
                >
                  <i className={`${benefit.icon} text-xl`} />
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2">
                  {t(benefit.titleKey)}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{t(benefit.textKey)}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div
            className={`mt-12 text-center transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ transitionDelay: '0.6s' }}
          >
            <Link
              to="/shape-your-project"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-md hover:bg-primary-600 transition-all hover:scale-[1.02] whitespace-nowrap"
            >
              {t('about.benefits.cta')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}